import { useMemo } from 'react';
import nonEmpty from '../utils/nonEmpty';
import type { TreeListTopic } from './toTopicTreeList';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import type { ToTopicTreeList_TopicFragment } from '../graphql/generated/graphql';

const USE_MEMBERSHIP_TOPICS_TOPIC_FRAGMENT = gql(`
  fragment useMembershipTopics_topic on Topic {
    id
  }
`);

type TopicFragment = DocumentType<typeof USE_MEMBERSHIP_TOPICS_TOPIC_FRAGMENT>;

export type TopicWithMembershipTopics<T> = T & {
  membershipChildTopics: TopicWithMembershipTopics<T>[];
};

const getMembershipTopics = <T extends ToTopicTreeList_TopicFragment>({
  allTopics,
  membershipIds,
}: {
  allTopics: TreeListTopic<T>[];
  membershipIds: string[];
}): TopicWithMembershipTopics<TreeListTopic<T>>[] => {
  if (allTopics.length === 0) return [];

  const topics: TopicWithMembershipTopics<TreeListTopic<T>>[] = [];
  const idsSet = new Set(membershipIds);
  const idsAccountedForSet = new Set();

  const prune = (
    topic: TreeListTopic<T>,
  ): TopicWithMembershipTopics<TreeListTopic<T>> => {
    const membershipChildTopics = topic.childTopics.filter(
      ({ id, inheritParent }) => (id && idsSet.has(id)) || inheritParent,
    );
    membershipChildTopics.forEach(({ id }) => idsAccountedForSet.add(id));

    return {
      ...topic,
      membershipChildTopics: membershipChildTopics.map((childTopic) =>
        prune(childTopic),
      ),
    };
  };

  const prunedTopics = membershipIds.map((id) => {
    const topic = allTopics.find(({ id: topicId }) => topicId === id);
    return topic ? prune(topic) : null;
  });

  prunedTopics.forEach((prunedTopic) => {
    if (!prunedTopic || idsAccountedForSet.has(prunedTopic.id)) return;
    topics.push(prunedTopic);
  });

  return topics;
};

const useMembershipTopics = <T extends ToTopicTreeList_TopicFragment>(
  allTopics?: TreeListTopic<T>[],
  directMembershipTopics?: Array<TopicFragment | null> | null,
): TopicWithMembershipTopics<TreeListTopic<T>>[] | null => {
  const membershipTopics = useMemo(() => {
    if (!directMembershipTopics || !allTopics) return null;

    return getMembershipTopics<T>({
      allTopics,
      membershipIds: directMembershipTopics
        .map((topic) => topic?.id)
        .filter(nonEmpty),
    });
  }, [directMembershipTopics, allTopics]);

  return membershipTopics;
};

export default useMembershipTopics;
